import { CommonModule, DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  OnDestroy,
  afterNextRender,
  inject,
  signal,
  computed,
} from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Meta, Title } from '@angular/platform-browser';
import {
  EnterprisePlan,
  PlansService,
  PricingFeature,
  PricingPlan,
} from '@app/core/services/plans.service';

const PAGE_TITLE = 'Pricing | Angularize, AI Angular App Builder with a Product Team';
const PAGE_DESCRIPTION =
  'Five AI agents build and deploy your Angular app, and a real Product Builder steps in when you need one. Plans from free to enterprise, with live URLs, instant rollback, and custom domains.';
const CANONICAL_PATH = '/pricing';

@Component({
  selector: 'app-pricing',
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './pricing.component.html',
  styleUrls: ['./pricing.component.css'],
})
export class PricingComponent implements OnDestroy {
  private plansService = inject(PlansService);
  private router = inject(Router);
  private host = inject(ElementRef<HTMLElement>);
  private document = inject(DOCUMENT);
  private titleService = inject(Title);
  private metaService = inject(Meta);

  billingCycle = signal<'monthly' | 'yearly'>('yearly');
  planType = signal<'individual' | 'enterprise'>('individual');
  angularServicesOpted = signal<boolean>(true);

  // Which cards currently have their secondary features expanded.
  private expandedPlans = signal<Set<string>>(new Set());

  isYearly = computed(() => this.billingCycle() === 'yearly');
  isEnterprise = computed(() => this.planType() === 'enterprise');

  private observer?: IntersectionObserver;
  private mutationObserver?: MutationObserver;
  private observedEls = new WeakSet<Element>();

  get individualPlans(): PricingPlan[] {
    return this.plansService.individualPlans;
  }
  get enterprisePlan(): EnterprisePlan {
    return this.plansService.enterprisePlan;
  }
  get activePlans(): PricingPlan[] {
    return this.individualPlans;
  }

  constructor() {
    // Runs on both server and browser. Safe to call during SSR: Title/Meta
    // write into the rendered <head>, which is what search crawlers read.
    this.setSeoTags();

    // afterNextRender only ever executes in the browser, after the first
    // render pass, so this replaces the old ngAfterViewInit + manual
    // `typeof window` checks. No DOM/window access ever runs on the server.
    afterNextRender(() => {
      const root = this.host.nativeElement;
      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches;

      // Elements behind *ngIf (the Enterprise card, cards-grid on toggle)
      // don't exist yet on first render, so a one-time querySelectorAll
      // would miss them. A MutationObserver keeps picking up newly added
      // .reveal elements whenever the Individual/Enterprise toggle fires.
      if (prefersReducedMotion || !('IntersectionObserver' in window)) {
        const revealAll = () => {
          (root.querySelectorAll('.reveal') as NodeListOf<HTMLElement>).forEach((el) =>
            el.classList.add('is-visible'),
          );
        };
        revealAll();
        this.mutationObserver = new MutationObserver(revealAll);
        this.mutationObserver.observe(root, { childList: true, subtree: true });
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              this.observer?.unobserve(entry.target);
            }
          }
        },
        { threshold: 0.15, rootMargin: '0px 0px -40px 0px' },
      );

      const observeNewRevealEls = () => {
        (root.querySelectorAll('.reveal') as NodeListOf<HTMLElement>).forEach((el) => {
          if (!this.observedEls.has(el)) {
            this.observedEls.add(el);
            this.observer?.observe(el);
          }
        });
      };

      observeNewRevealEls();

      this.mutationObserver = new MutationObserver(observeNewRevealEls);
      this.mutationObserver.observe(root, { childList: true, subtree: true });
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    this.mutationObserver?.disconnect();
  }

  private setSeoTags(): void {
    this.titleService.setTitle(PAGE_TITLE);

    this.metaService.updateTag({ name: 'description', content: PAGE_DESCRIPTION });
    this.metaService.updateTag({ property: 'og:title', content: PAGE_TITLE });
    this.metaService.updateTag({ property: 'og:description', content: PAGE_DESCRIPTION });
    this.metaService.updateTag({ property: 'og:type', content: 'website' });
    this.metaService.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.metaService.updateTag({ name: 'twitter:title', content: PAGE_TITLE });
    this.metaService.updateTag({ name: 'twitter:description', content: PAGE_DESCRIPTION });

    const origin = this.document.location?.origin ?? 'https://angularize.dev';
    const canonicalUrl = `${origin}${CANONICAL_PATH}`;
    this.metaService.updateTag({ property: 'og:url', content: canonicalUrl });

    let link: HTMLLinkElement | null = this.document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);
  }

  getPrice(plan: PricingPlan): string {
    return this.plansService.getDisplayPrice(plan, this.isYearly());
  }

  getSavings(plan: PricingPlan): number | null {
    return this.plansService.getAnnualSavings(plan);
  }

  isExpanded(planId: string): boolean {
    return this.expandedPlans().has(planId);
  }

  toggleExpand(planId: string): void {
    const next = new Set(this.expandedPlans());
    if (next.has(planId)) {
      next.delete(planId);
    } else {
      next.add(planId);
    }
    this.expandedPlans.set(next);
  }

  hiddenFeatureCount(plan: { features: PricingFeature[] }): number {
    return plan.features.filter((f) => f.hidden && !f.isCheckbox).length;
  }

  navigateToCheckout(plan: PricingPlan) {
    if (plan.id === 'free') {
      this.router.navigate(['/login']);
      return;
    }
    this.router.navigate(['/checkout'], {
      queryParams: {
        plan: plan.id,
        billing: this.billingCycle(),
      },
    });
  }

  setBilling(cycle: 'monthly' | 'yearly') {
    this.billingCycle.set(cycle);
  }
  setPlanType(type: 'individual' | 'enterprise') {
    this.planType.set(type);
  }
}